import { Link } from 'react-router-dom';
import { Calendar, Trash2, Edit2 as Edit, Table, Mail, X, Plus, Users, Loader2, FileDown, History, FileText, Archive, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { tailwindTextToHex, tailwindTextToBg } from '@/lib/utils';
import { getStatusConfig, getWeekNumber, API } from './helpers';
import { VersionHistoryModal } from './VersionHistoryModal';
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';

/**
 * Card for displaying completed bulk registrations
 */
export function BulkRegistrationCard({ bulk, isDemo, onRefresh, onDelete }) {
  const statusConfig = getStatusConfig(bulk.status);
  const StatusIcon = statusConfig.icon;
  const isSent = bulk.status === 'sent' || bulk.sent_to_customer;

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [customEmails, setCustomEmails] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [recipientCount, setRecipientCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showSummaryEdit, setShowSummaryEdit] = useState(false);
  const [editSummary, setEditSummary] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);
  const [showPdfArchive, setShowPdfArchive] = useState(false);
  const [pdfArchive, setPdfArchive] = useState([]);
  const [loadingArchive, setLoadingArchive] = useState(false);

  const handlePreviewPdf = async () => {
    setLoadingPdf(true);
    try {
      const res = await axios.get(`${API}/reports/bulk/${bulk.id}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      // Extract error message from blob response
      let errorMsg = 'Kunne ikkje opne PDF-rapport';
      try {
        if (err.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.detail || errorMsg;
        } else if (err.response?.data?.detail) {
          errorMsg = err.response.data.detail;
        }
      } catch {}
      console.error('PDF error:', errorMsg, err.response?.status);
      toast.error(errorMsg);
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleSendToCustomer = async () => {
    try {
      const updatedBulk = { ...bulk, status: 'sent' };
      
      if (isDemo) {
        const existingBulks = JSON.parse(localStorage.getItem('demo_bulk_registrations') || '[]');
        const index = existingBulks.findIndex(b => b.id === bulk.id);
        if (index !== -1) {
          existingBulks[index] = updatedBulk;
          localStorage.setItem('demo_bulk_registrations', JSON.stringify(existingBulks));
          toast.success('Sammendrag sendt til kunde!');
          onRefresh();
        }
      } else {
        await axios.put(`${API}/bulk-registrations/${bulk.id}`, updatedBulk, { withCredentials: true });
        toast.success('Sammendrag sendt til kunde!');
        onRefresh();
      }
    } catch (error) {
      console.error('Error sending to customer:', error);
      toast.error('Feil ved sending til kunde');
    }
  };

  const openEmailModal = async () => {
    setShowEmailModal(true);
    setLoadingRecipients(true);
    try {
      const res = await axios.get(`${API}/bulk-registrations/${bulk.id}/recipients`, { withCredentials: true });
      setRecipientCount(res.data.count || 0);
    } catch {
      setRecipientCount(0);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const addEmail = () => {
    const email = emailInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !customEmails.includes(email)) {
      setCustomEmails(prev => [...prev, email]);
      setEmailInput('');
    }
  };

  const removeEmail = (email) => {
    setCustomEmails(prev => prev.filter(e => e !== email));
  };

  const handleSendEmail = async () => {
    if (!sendToAll && customEmails.length === 0) {
      toast.error('Legg til minst éin e-postadresse eller vel «Send til alle»');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/bulk-registrations/${bulk.id}/send-email`, {
        emails: customEmails,
        send_to_all: sendToAll,
      }, { withCredentials: true });
      toast.success(res.data.message || 'E-post sendt!');
      setShowEmailModal(false);
      setCustomEmails([]);
      setSendToAll(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Feil ved sending av e-post');
    } finally {
      setSending(false);
    }
  };

  const formatSummary = () => {
    const savedBulks = JSON.parse(localStorage.getItem('demo_bulk_registrations') || '[]');
    const fullBulk = savedBulks.find(b => b.id === bulk.id);
    const aiSummary = fullBulk?.ai_summary || bulk.summary || 'Ingen sammendrag tilgjengelig';
    
    let cleanText = aiSummary
      .replace(/\*\*Dato:\*\*/g, '')
      .replace(/\*\*Hovedfunn:\*\*/g, '')
      .replace(/\*\*/g, '')
      .replace(/^#+\s*/gm, '')
      .replace(/\\n\\n+/g, ' • ')
      .replace(/\\n/g, ' ')
      .trim();
    
    cleanText = cleanText.replace(/^[^•]+(P\d+\s+\w+)?\s*\d{1,2}\.\d{1,2}\.\d{4}\s*/, '');
    
    if (cleanText.length > 180) {
      cleanText = cleanText.substring(0, 177) + '...';
    }
    
    return cleanText;
  };

  return (
    <>
      <Card 
        className="shadow-sm hover:shadow-md transition-all border border-slate-200 rounded-xl overflow-hidden"
      >
        <CardContent className="p-0">
          {/* Top bar with clickable status */}
          <div className={`flex items-center justify-between px-5 py-3 ${statusConfig.bgColor} border-b border-slate-100`}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer" data-testid={`status-trigger-${bulk.id}`}>
                  <StatusIcon className={`w-5 h-5 ${statusConfig.iconColor}`} />
                  <span className={`text-sm font-semibold ${statusConfig.iconColor}`}>{statusConfig.label}</span>
                  <ChevronDown className={`w-3.5 h-3.5 ${statusConfig.iconColor} opacity-50`} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                {[
                  { value: 'draft', label: 'Utkast (ikkje sendt)', icon: FileText, color: 'text-amber-600' },
                  { value: 'sent', label: 'Sendt til kunde', icon: Mail, color: 'text-teal-600' },
                ].map(opt => (
                  <DropdownMenuItem
                    key={opt.value}
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/bulk-registrations/${bulk.id}/status`, { status: opt.value }, { withCredentials: true });
                        toast.success(`Status endra til "${opt.label}"`);
                        if (onRefresh) onRefresh();
                      } catch { toast.error('Kunne ikkje endre status'); }
                    }}
                    className={`cursor-pointer ${bulk.status === opt.value ? 'bg-slate-100 font-medium' : ''}`}
                    data-testid={`status-option-${opt.value}`}
                  >
                    <opt.icon className={`w-4 h-4 mr-2 ${opt.color}`} /> {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex items-center gap-2">
              {isSent && (
                <div className="flex items-center gap-2" style={{ display: 'contents' }}>
                  <Button variant="outline" size="sm" onClick={handlePreviewPdf} disabled={loadingPdf}
                    data-testid={`preview-pdf-btn-${bulk.id}`}
                    className="h-8 text-xs text-orange-600 border-orange-200 hover:bg-orange-50">
                    {loadingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1" />}
                    PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={async () => {
                    setShowPdfArchive(true);
                    setLoadingArchive(true);
                    try {
                      const res = await axios.get(`${API}/reports/pdf-archive?bulk_id=${bulk.id}`, { withCredentials: true });
                      setPdfArchive(res.data || []);
                    } catch { setPdfArchive([]); }
                    finally { setLoadingArchive(false); }
                  }}
                    className="h-8 text-xs text-violet-600 border-violet-200 hover:bg-violet-50">
                    <Archive className="w-3.5 h-3.5 mr-1" /> Arkiv
                  </Button>
                  <Button variant="outline" size="sm" onClick={openEmailModal}
                    data-testid={`send-email-btn-${bulk.id}`}
                    className="h-8 text-xs text-blue-600 border-blue-200 hover:bg-blue-50">
                    <Mail className="w-3.5 h-3.5 mr-1" /> E-post
                  </Button>
                </div>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowVersionHistory(true)}
                data-testid={`version-history-btn-${bulk.id}`}
                className="h-8 text-xs text-indigo-600 border-indigo-200 hover:bg-indigo-50">
                <History className="w-3.5 h-3.5 mr-1" /> Historikk
              </Button>
              <Link to={`/admin/bulk-edit/${bulk.id}`}>
                <Button variant="outline" size="sm" className="h-8 text-xs">
                  <Edit className="w-3.5 h-3.5 mr-1" /> Rediger
                </Button>
              </Link>
              <Link to={`/admin/bulk-view/${bulk.id}`}>
                <Button size="sm" className="h-8 text-xs bg-teal-600 hover:bg-teal-700">
                  <Table className="w-3.5 h-3.5 mr-1" /> Detaljer
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={() => onDelete(bulk.id)}
                className="h-8 w-8 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
          
          {/* Content */}
          <div className="px-5 py-4">
            <div className="flex items-start gap-4">
              <div className="flex-1 min-w-0">
                {/* Date + metadata row */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600 mb-2">
                  <span className="flex items-center gap-1.5 font-medium text-slate-900">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    {new Date(bulk.date).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                    Uke {getWeekNumber(bulk.date)}
                  </span>
                  <span className="text-slate-400">·</span>
                  <span>{bulk.area_name}</span>
                  <span className="text-slate-400">·</span>
                  <span>{bulk.sample_count} stasjonar</span>
                  <span className="text-slate-400">·</span>
                  <span>{bulk.biologist}</span>
                  {bulk.created_by_email && (
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <span className="text-slate-400">·</span>
                      <Users className="w-3 h-3" /> {bulk.created_by_email}
                    </span>
                  )}
                </div>
                
                {/* Summary - click to edit */}
                <button
                  onClick={() => { setEditSummary(bulk.ai_summary || bulk.summary || ''); setShowSummaryEdit(true); }}
                  className="text-sm text-slate-500 line-clamp-2 text-left hover:text-slate-700 cursor-pointer group/sum"
                  data-testid={`edit-summary-${bulk.id}`}
                >
                  {formatSummary()}
                  <Edit className="w-3 h-3 inline ml-1 opacity-0 group-hover/sum:opacity-100 transition-opacity text-slate-400" />
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Version history modal */}
      {showVersionHistory && (
        <VersionHistoryModal
          bulkId={bulk.id}
          bulkLabel={`${bulk.area_name} — ${bulk.date}`}
          onClose={() => setShowVersionHistory(false)}
          onRestore={onRefresh}
        />
      )}

      {/* Email sending modal */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowEmailModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="email-modal">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Send rapport på e-post</h3>
                  <p className="text-xs text-slate-500">{bulk.area_name} — {bulk.date}</p>
                </div>
              </div>
              <button onClick={() => setShowEmailModal(false)} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Option 1: Send to all */}
              <button
                onClick={() => setSendToAll(!sendToAll)}
                data-testid="send-to-all-toggle"
                className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all text-left ${
                  sendToAll
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  sendToAll ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                }`}>
                  {sendToAll && <div className="w-2 h-2 bg-white rounded-full" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-500" />
                    <span className="font-medium text-slate-900 text-sm">Send til alle mottakarar for området</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {loadingRecipients ? 'Hentar...' : `${recipientCount ?? '?'} kundebrukarar med tilgang til ${bulk.area_name}`}
                  </p>
                </div>
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400 font-medium">OG / ELLER</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Option 2: Custom emails */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Legg til enkeltadresser</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={e => setEmailInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                    placeholder="namn@bedrift.no"
                    className="flex-1 h-10 px-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="email-input"
                  />
                  <Button onClick={addEmail} size="sm" variant="outline" className="h-10 px-3" data-testid="add-email-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Email chips */}
                {customEmails.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {customEmails.map(email => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium border border-blue-200"
                      >
                        {email}
                        <button onClick={() => removeEmail(email)} className="hover:text-blue-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50">
              <p className="text-xs text-slate-400">
                PDF-rapport vert generert og vedlagt automatisk
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowEmailModal(false)}>Avbryt</Button>
                <Button
                  size="sm"
                  onClick={handleSendEmail}
                  disabled={sending || (!sendToAll && customEmails.length === 0)}
                  data-testid="confirm-send-email-btn"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {sending ? (
                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sender...</>
                  ) : (
                    <><Mail className="w-4 h-4 mr-1.5" /> Send e-post</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Archive modal */}
      {showPdfArchive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowPdfArchive(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-violet-100 rounded-lg flex items-center justify-center">
                  <Archive className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">PDF-arkiv</h3>
                  <p className="text-xs text-slate-500">{bulk.area_name} — {bulk.date}</p>
                </div>
              </div>
              <button onClick={() => setShowPdfArchive(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 max-h-[50vh] overflow-y-auto">
              {loadingArchive ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
              ) : pdfArchive.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">Ingen arkiverte PDF-ar enno. Generer ein PDF for å lagre den.</p>
              ) : (
                <div className="space-y-2">
                  {pdfArchive.map(pdf => (
                    <a key={pdf.id} href={`${API}/reports/pdf-archive/${pdf.id}/download`}
                      className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-violet-300 hover:bg-violet-50/30 transition-all group">
                      <FileDown className="w-5 h-5 text-violet-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">
                          {new Date(pdf.generated_at).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {' '}kl. {new Date(pdf.generated_at).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p className="text-xs text-slate-400">{pdf.generated_by_email} · {Math.round(pdf.file_size / 1024)} KB</p>
                      </div>
                      <FileDown className="w-4 h-4 text-slate-300 group-hover:text-violet-500 transition-colors shrink-0" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Summary edit modal */}
      {showSummaryEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowSummaryEdit(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-testid="summary-edit-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900">Rediger sammendrag</h3>
                  <p className="text-xs text-slate-500">{bulk.area_name} — {bulk.date}</p>
                </div>
              </div>
              <button onClick={() => setShowSummaryEdit(false)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <RichTextEditor
                content={editSummary}
                onChange={setEditSummary}
                placeholder="Skriv eit sammendrag av prøveresultata..."
              />
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 shrink-0">
              <Button variant="outline" size="sm" onClick={() => setShowSummaryEdit(false)}>Avbryt</Button>
              <Button
                size="sm"
                disabled={savingSummary}
                data-testid="save-summary-btn"
                className="bg-teal-600 hover:bg-teal-700"
                onClick={async () => {
                  setSavingSummary(true);
                  try {
                    await axios.put(`${API}/bulk-registrations/${bulk.id}`, {
                      ...bulk,
                      ai_summary: editSummary
                    }, { withCredentials: true });
                    toast.success('Sammendrag lagra');
                    bulk.ai_summary = editSummary;
                    setShowSummaryEdit(false);
                    if (onRefresh) onRefresh();
                  } catch {
                    toast.error('Kunne ikkje lagre');
                  } finally { setSavingSummary(false); }
                }}
              >
                {savingSummary ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
                Lagre
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
